// Base64 upload button (s105 DB-1) — the replacement for the retired Uppy-based
// `ObjectUploader`. Instead of a browser→R2 presigned PUT, the chosen file(s) are read
// as base64 and handed to `onUploadFile`, whose host POSTs to the server-side base64
// attach route (`/api/generic-entries/:id/documents/upload`). No CORS, no presign, no
// second finalize call. Files are uploaded sequentially; the button shows a spinner
// while in flight. Presentational + controlled: the host owns the network call.

import { useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { DOCUMENT_ACCEPT } from "@/lib/upload-accept";

interface Base64UploadButtonProps {
  /** Called once per selected file. Should resolve when the upload completes. */
  onUploadFile: (file: File) => Promise<void>;
  accept?: string;
  /** Max files per selection (0/undefined = unlimited). */
  maxNumberOfFiles?: number;
  disabled?: boolean;
  buttonVariant?: "default" | "outline" | "secondary" | "ghost" | "destructive";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  buttonClassName?: string;
  children: ReactNode;
}

export function Base64UploadButton({
  onUploadFile,
  accept = DOCUMENT_ACCEPT,
  maxNumberOfFiles,
  disabled = false,
  buttonVariant = "outline",
  buttonSize = "sm",
  buttonClassName,
  children,
}: Base64UploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, maxNumberOfFiles && maxNumberOfFiles > 0 ? maxNumberOfFiles : undefined);
    setUploading(true);
    try {
      for (const file of files) {
        await onUploadFile(file);
      }
    } finally {
      setUploading(false);
      // Reset so selecting the same file again re-fires onChange.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={maxNumberOfFiles !== 1}
        className="hidden"
        onChange={handleChange}
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={buttonClassName}
        variant={buttonVariant}
        size={buttonSize}
        disabled={disabled || uploading}
        data-testid="button-upload"
      >
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        {children}
      </Button>
    </>
  );
}
